import React from "react";
//#region types
import { SalaryDeviationFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { ACTION_SEPARATOR } from "models/propTypes/menuPropTypes";
import { WintSuperkollAPIModelCustomFilters } from "wint-api-client/dist/types";
//#endregion
//#region components
import {
  DashboardButtonGroup,
  FilterButton,
  PageLayouts,
  ReloadEvent,
  Skeletons,
  useMountedState,
} from "components/common";
import useSalaryDeviationReportFilterDefinition from "./forms/useReportFilterDefinition";
import SalaryDeviationListContainer from "./listContainer";
import CreateDeviationsButton from "./createButton";
import SalaryDeviationReportFooter from "./reportFooter";
import DeviationMonth from "./deviationMonth";
import { Grid, Portal } from "@mui/material";
import { useEmployeeActions } from "components/employee";
import { useSalaryDeviationsService } from "serviceHooks";
import useSalaryPermissions from "components/salary/hooks/useSalaryPermissions";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const SalaryDeviationReport: React.FC = (props) => {
  const salaryDeviationsService = useSalaryDeviationsService();
  const {
    filterFormFields: reportFilterDefinition,
    filterQueryStateHandler,
    apiToReportFilterFormatter,
  } = useSalaryDeviationReportFilterDefinition();

  const [initialFilter, setInitialFilter] = React.useState<SalaryDeviationFormTypes.Filter>();
  const { getMounted } = useMountedState();

  React.useEffect(() => {
    salaryDeviationsService.getMonthToApprove().then((res) => {
      if (getMounted()) {
        setInitialFilter({
          yearAndMonth: res,
        });
      }
    });
  }, []);

  const { employeeSettingsMoreAction, addEmployeeMoreAction } = useEmployeeActions();
  const { isSalaryDeviationsAvailable } = useSalaryPermissions();

  const footerMount = () => document.getElementById("reportFooterContainer");

  return initialFilter ? (
    <ReloadEvent
      render={(reloadKey, reload) => (
        <PageLayouts.DashboardLayout<SalaryDeviationFormTypes.Filter>
          entityKey={0}
          hasPermissions={isSalaryDeviationsAvailable}
          title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.Deviations)}
          topRightButtons={
            <DashboardButtonGroup
              buttons={[
                <CreateDeviationsButton reloadKey={reloadKey} reloadList={reload} />,
                ACTION_SEPARATOR,
                <FilterButton />,
              ]}
              moreActions={[addEmployeeMoreAction, employeeSettingsMoreAction]}
            />
          }
          filterFields={reportFilterDefinition}
          filterQueryStateHandler={filterQueryStateHandler}
          filterCategory={WintSuperkollAPIModelCustomFilters.FilterCategory.SalaryDeviation}
          reportToAPIFilterFormatter={salaryDeviationsService.reportToAPIFilter}
          apiToReportFilterFormatter={apiToReportFilterFormatter}
          initialFilter={initialFilter}
          className={cssExports.fullHeight}
        >
          <DeviationMonth
            reloadKey={reloadKey}
            render={(deviationMonth, isAllowedToSubmit) => (
              <>
                <Grid container item>
                  <SalaryDeviationListContainer
                    reloadKey={reloadKey}
                    reloadList={reload}
                    deviationMonth={deviationMonth}
                  />
                </Grid>
                <Portal container={footerMount()}>
                  <SalaryDeviationReportFooter
                    reloadKey={reloadKey}
                    reloadList={reload}
                    deviationMonth={deviationMonth}
                    isAllowedToSubmit={isAllowedToSubmit}
                  />
                </Portal>
              </>
            )}
          />
        </PageLayouts.DashboardLayout>
      )}
    />
  ) : (
    <PageLayouts.DashboardLayout<SalaryDeviationFormTypes.Filter>
      entityKey={0}
      hasPermissions={isSalaryDeviationsAvailable}
      title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.Deviations)}
    >
      <Skeletons.TableSkeleton />
    </PageLayouts.DashboardLayout>
  );
};

export default SalaryDeviationReport;
